import PageMainTitle from 'components/commons/PageMainTitle'
import { pageMainTitle } from 'components/cases/Case/CaseItems'
import styles from 'components/cases/listpage/ListPage.module.scss'
import Link from 'next/link'
import Button from 'components/commons/Button/Button'
import Pagination from 'components/topics/Pagination'
import SearchTagList from 'components/cases/listpage/common/SearchTagList'
import { useState, useEffect, useRef } from 'react'
import { gsap, Power4 } from 'gsap'
import { ScrollTrigger } from 'gsap/dist/ScrollTrigger'
import MailmagazineLink from 'components/mailmagazine/MailmagazineLink/MailmagazineLink'
import { caseStudyItems } from './CaseStudyItem'

gsap.registerPlugin(ScrollTrigger)

const Cases: React.FC<{ pageId: number }> = ({ pageId }) => {
  const paths = [
    { name: 'TOP', url: '/' },
    { name: 'Case study', url: '#' },
  ]

  const [isOpen, setIsOpen] = useState(false)
  const pageSize = 10
  const totalPages = Math.ceil(caseStudyItems.length / pageSize)
  const currentPage = pageId || 1

  const getPageLink = (pageNumber) => `/cases/page/${pageNumber}`

  const currentItems = caseStudyItems.slice((currentPage - 1) * pageSize, currentPage * pageSize)

  const navRef = useRef<HTMLDivElement | null>(null)
  const sectionRef = useRef<HTMLUListElement | null>(null)

  useEffect(() => {
    const elements = sectionRef.current?.querySelectorAll('li') || []

    gsap.set(elements, { opacity: 0, y: 100 })

    // 読み込み時にアニメーションを開始
    gsap.to(elements, {
      opacity: 1,
      y: 0,
      duration: 0.8,
      stagger: 0.1,
      ease: Power4.easeOut,
    })

    // navRefそのものにアニメーションをかける
    gsap.set(navRef.current, { opacity: 0, y: 100 })

    gsap.to(navRef.current, {
      opacity: 1,
      y: 0,
      duration: 0.8,
      ease: Power4.easeOut,
      scrollTrigger: {
        trigger: navRef.current,
        start: 'top 80%',
        toggleActions: 'play none none play',
      },
    })
  }, [])

  const list = currentItems.map(({ projectName, client, id, tags, src, alt, linkToDetail }): JSX.Element => {
    const content = (
      <>
        {linkToDetail ? (
          <Link href={linkToDetail}>
            <a className={styles.caseitem_pic} href={linkToDetail}>
              <figure className={styles.caseitem_pic_img}>
                <img src={src} alt={alt} className={styles.caseitem_pic_img_item} />
              </figure>
            </a>
          </Link>
        ) : (
          <div className={styles.caseitem_pic}>
            <figure className={styles.caseitem_pic_img}>
              <img src={src} alt={alt} className={styles.caseitem_pic_img_item} />
            </figure>
          </div>
        )}
        <div className={styles.caseitem_info}>
          {linkToDetail ? (
            <Link href={linkToDetail}>
              <a className={styles.caseitem_main} href={linkToDetail}>
                <div className={styles.caseitem_main_project}>{projectName}</div>
                <div className={styles.caseitem_main_client}>{client}</div>
              </a>
            </Link>
          ) : (
            <div className={styles.caseitem_main}>
              <div className={styles.caseitem_main_project}>{projectName}</div>
              <div className={styles.caseitem_main_client}>{client}</div>
            </div>
          )}
          <ul className={styles.caseitem_tag}>
            {tags.map((tagItem, index) => (
              <li
                key={`${tagItem.tag}-${index}`}
                className={`${
                  [
                    'IT/情報/通信',
                    'メーカー/製造/建設',
                    '金融/不動産',
                    '小売/流通/卸',
                    'サービス',
                    '公共/行政',
                  ].includes(tagItem.tag)
                    ? styles.caseitem_tag_white
                    : styles.caseitem_tag_gray
                }`}
              >
                <a href={tagItem.href} className={styles.categoryButton} onClick={(e) => e.stopPropagation()}>
                  {tagItem.tag}
                </a>
              </li>
            ))}
          </ul>
        </div>
      </>
    )

    return (
      <li key={id} className={styles.caseitem}>
        {content}
      </li>
    )
  })

  return (
    <article>
      <PageMainTitle pageMainTitle={pageMainTitle} paths={paths} />
      <div className={styles.postContent}>
        <button className={`${styles.postContent_accordion} accordionBtn`} onClick={() => setIsOpen(!isOpen)}>
          <span>サービス・業界から探す</span>
        </button>
        <div ref={navRef} className={`${styles.postContent_nav} accordionBody`} aria-hidden={!isOpen}>
          <div className={`${styles.allbtn} ${styles.is_active}`}>すべてのサービス・業界</div>
          <SearchTagList currentCategory="" />
          <div className={styles.contactPanel}>
            <p className={styles.contactPanel_ttl}>Contact</p>
            <p className={styles.contactPanel_desc}>お気軽にお問い合わせください</p>
            <div className={styles.contactPanel_btn}>
              <Button text="お問い合わせ" href="/contact/" red="red" />
            </div>
          </div>
        </div>
        {totalPages > 1 && (
          <div className={`${styles.pagination} ${styles.is_sp}`}>
            <Pagination currentPage={currentPage} totalPages={totalPages} getPageLink={getPageLink} />
          </div>
        )}
        <div className={styles.postContent_content}>
          <ul className={styles.caselist} ref={sectionRef}>
            {list}
          </ul>
          {totalPages > 1 && (
            <div className={styles.pagination}>
              <Pagination currentPage={currentPage} totalPages={totalPages} getPageLink={getPageLink} />
            </div>
          )}
        </div>
      </div>
      <MailmagazineLink />
      {/* eslint-disable-next-line react/no-unknown-property */}
      <style jsx>{`
        @media (max-width: 719px) {
          .accordionBtn {
            border-bottom: ${isOpen ? 'unset' : '1px solid #444444'};
          }
          .accordionBtn::before {
            transform: ${isOpen ? 'rotate(180deg)' : 'rotate(0deg)'};
          }
          .accordionBody {
            max-height: ${isOpen ? '9999px' : 0};
            transition: all 0.3s;
            overflow: hidden;
          }
        }
      `}</style>
    </article>
  )
}

export default Cases
